import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Always use relative URLs for API calls — works on any domain (dalphaprime.com, preview URL, etc.)
function apiUrl(path) {
  return path;
}
const TOKEN_KEY = "dalfa_access_token";

const AuthContext = createContext(null);

// Set global axios interceptor for auth
function setupAxiosAuth(token) {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=not auth, object=user
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);

  const checkSession = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    setupAxiosAuth(token);
    try {
      const { data } = await axios.get(apiUrl("/api/auth/me"));
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setupAxiosAuth(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      checkSession();
    }
  }, [checkSession]);

  const login = async (email, password) => {
    console.log("[DALFA] Login attempt:", email);
    const resp = await axios.post(apiUrl("/api/auth/login"), { email, password });
    const data = resp.data;
    console.log("[DALFA] Login response OK, token:", !!data.token);
    if (data.token) {
      try {
        localStorage.setItem(TOKEN_KEY, data.token);
      } catch (e) {
        console.warn("[DALFA] localStorage blocked:", e.message);
      }
      setupAxiosAuth(data.token);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    localStorage.removeItem(TOKEN_KEY);
    setupAxiosAuth(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
